import React, { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { ReactComponent as ArrowUp } from "../../../../assets/icons/svg/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../../assets/icons/svg/arrow-down.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/svg/cancel-icon.svg";
import { ReactComponent as WifiLiveIcon } from "../../../../assets/icons/svg/wifi-live-icon.svg";
import { ReactComponent as WifiOfflineIcon } from "../../../../assets/icons/svg/wifi-offline-icon.svg";
import { useGroups } from "../../../../context/GroupContext";

interface Screen {
  id: string;
  name: string;
  group: string;
  orientation: string;
  tags: { tag: string; value: string }[];
  status: string;
  createdAt: Date;
}

interface FilterState {
  status: string[];
  groups: string[];
  orientations: string[];
  tags: string[];
  cities: string[];
}

interface ItemListPopupProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

const ItemListPopup: React.FC<ItemListPopupProps> = ({
  title,
  onClose,
  children,
  className = "",
}) => {
  return (
    <div
      className={`
        fixed top-0 right-0 z-50 h-screen bg-white
        shadow-[0px_6px_24px_0px_rgba(0,0,0,0.15)] pl-6 pr-3 pt-6 pb-6
        flex flex-col transition-all duration-300 ease-in-out
        rounded-tl-xl rounded-bl-xl overflow-hidden
        w-full sm:w-[50%] md:w-[40%] lg:w-[35%] xl:w-[25%] 2xl:w-[25%] 3xl:w-[23%]
        ${className}`}
    >
      <header className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold sm:text-sm md:text-lg">{title}</h2>
        <button
          onClick={onClose}
          aria-label="Close popup"
          className="transition-colors hover:text-red-500"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </header>
      <main className="flex-1 text-sm sm:text-base overflow-y-auto">
        {children}
      </main>
    </div>
  );
};

interface ScreenFilterPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterState) => void;
  screens: Screen[];
  currentFilters: FilterState;
}

const ScreenFilterPopup: React.FC<ScreenFilterPopupProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  screens,
  currentFilters,
}) => {
  const { groups } = useGroups();
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({
    group: "",
    orientation: "",
    tags: "",
  });

  const getUniqueValues = () => {
    const groupMap = new Map<string, string>(
      groups.map((group) => [group.id, group.name])
    );
    const groupNames = Array.from(
      new Set(
        screens
          .map((s) => s.group)
          .filter((id) => id && groupMap.has(id))
          .map((id) => groupMap.get(id)!)
      )
    ).sort();

    const orientations = Array.from(
      new Set(screens.map((s) => s.orientation).filter(Boolean))
    ).sort();

    const tagCategories = new Map<string, Set<string>>();
    screens.forEach((screen) => {
      if (screen.tags && screen.tags.length > 0) {
        screen.tags.forEach((tag) => {
          if (tag.tag && tag.value) {
            const categoryKey = tag.tag.toLowerCase();
            if (!tagCategories.has(categoryKey)) {
              tagCategories.set(categoryKey, new Set());
            }
            tagCategories.get(categoryKey)?.add(tag.value);
          }
        });
      }
    });

    const tags = Array.from(tagCategories.entries())
      .map(([categoryKey, values]) => {
        const originalCategory =
          screens
            .flatMap((s) => s.tags || [])
            .find((t) => t.tag.toLowerCase() === categoryKey)?.tag ||
          categoryKey;
        return {
          category: originalCategory,
          values: Array.from(values).sort(),
        };
      })
      .sort((a, b) => a.category.localeCompare(b.category));

    return { groups: groupNames, orientations, tags };
  };

  const { groups: groupNames, orientations, tags } = getUniqueValues();

  const getFilteredItems = (items: string[], searchTerm: string) => {
    return items.filter((item) =>
      item.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getFilteredTags = (
    tagList: { category: string; values: string[] }[],
    searchTerm: string
  ) => {
    if (!searchTerm) return tagList;
    return tagList
      .map((tagGroup) => ({
        ...tagGroup,
        values: tagGroup.values.filter(
          (value) =>
            value.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tagGroup.category.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      }))
      .filter((tagGroup) => tagGroup.values.length > 0);
  };

  const handleStatusToggle = (status: string) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleFilterToggle = (category: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((v) => v !== value)
        : [...prev[category], value],
    }));
  };

  const handleClearAll = () => {
    const clearedFilters = {
      status: [],
      groups: [],
      orientations: [],
      tags: [],
      cities: [],
    };
    setFilters(clearedFilters);
    onApplyFilters(clearedFilters);
    onClose();
  };

  const handleApply = () => {
    const groupMap = new Map<string, string>(
      groups.map((group) => [group.name, group.id])
    );
    const filtersWithGroupIds = {
      ...filters,
      groups: filters.groups
        .filter((name) => groupMap.has(name))
        .map((name) => groupMap.get(name)!),
    };
    onApplyFilters(filtersWithGroupIds);
    onClose();
  };

  const handleCancel = () => {
    setFilters(currentFilters);
    onClose();
  };

  const toggleCategory = (category: string) => {
    setOpenCategory((prev) => (prev === category ? null : category));
  };

  const handleSearchChange = (category: string, value: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [category]: value,
    }));
  };

  const getAllSelectedFilters = () => {
    return [
      ...filters.status,
      ...filters.groups,
      ...filters.orientations,
      ...filters.tags,
    ];
  };

  const removeFilter = (filterValue: string) => {
    setFilters((prev) => ({
      status: prev.status.filter((s) => s !== filterValue),
      groups: prev.groups.filter((g) => g !== filterValue),
      orientations: prev.orientations.filter((o) => o !== filterValue),
      tags: prev.tags.filter((t) => t !== filterValue),
      cities: prev.cities.filter((c) => c !== filterValue),
    }));
  };

  const selectedFilters = getAllSelectedFilters();

  if (!isOpen) return null;

  const FilterContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-gray-700">
          Selected ({selectedFilters.length})
        </p>
        {selectedFilters.length > 0 && (
          <button
            onClick={handleClearAll}
            className="text-sm text-red-500 hover:text-red-600"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {selectedFilters.map((filter) => (
          <div
            key={filter}
            className="flex items-center px-2 py-1 text-sm bg-[#F8F8F8] rounded-[10px] border-[0.5px] border-[#808191]"
          >
            {filter}
            <button
              onClick={() => removeFilter(filter)}
              className="ml-2 text-gray-600 hover:text-red-600"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="pt-4 border-t mt-4" />

      <div className="mb-6">
        <h3 className="text-sm font-medium mb-3">Screen status:</h3>
        <div className="flex gap-2">
          <button
            onClick={() => handleStatusToggle("live")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border flex-1 text-sm ${
              filters.status.includes("live")
                ? "bg-[#E6F9FF] border-[#0BA6C7] text-[#0BA6C7]"
                : "bg-white border-gray-300 text-gray-700"
            }`}
          >
            <WifiLiveIcon className="w-4 h-4" />
            Live
          </button>
          <button
            onClick={() => handleStatusToggle("offline")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border flex-1 text-sm ${
              filters.status.includes("offline")
                ? "bg-[#E6F9FF] border-[#0BA6C7] text-[#0BA6C7]"
                : "bg-white border-gray-300 text-gray-700"
            }`}
          >
            <WifiOfflineIcon className="w-4 h-4" />
            Offline
          </button>
        </div>
      </div>

      <div className="space-y-4 flex-1 overflow-y-auto">
        {groupNames.length > 0 && (
          <div className="rounded-[10px] p-3 bg-[#F8F8F8]">
            <button
              onClick={() => toggleCategory("group")}
              className="w-full flex justify-between items-center text-left text-sm font-medium"
            >
              Group
              {openCategory === "group" ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
            </button>
            {openCategory === "group" && (
              <div className="mt-3 space-y-3">
                <div className="relative">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerms.group}
                    onChange={(e) =>
                      handleSearchChange("group", e.target.value)
                    }
                    placeholder="Search groups..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {getFilteredItems(groupNames, searchTerms.group).map(
                    (groupName) => (
                      <label
                        key={groupName}
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={filters.groups.includes(groupName)}
                          onChange={() =>
                            handleFilterToggle("groups", groupName)
                          }
                          className="accent-[#0BA6C7]"
                        />
                        {groupName}
                      </label>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {orientations.length > 0 && (
          <div className="rounded-[10px] p-3 bg-[#F8F8F8]">
            <button
              onClick={() => toggleCategory("orientation")}
              className="w-full flex justify-between items-center text-left text-sm font-medium"
            >
              Orientation
              {openCategory === "orientation" ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
            </button>
            {openCategory === "orientation" && (
              <div className="mt-3 space-y-3">
                <div className="relative">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerms.orientation}
                    onChange={(e) =>
                      handleSearchChange("orientation", e.target.value)
                    }
                    placeholder="Search orientations..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {getFilteredItems(orientations, searchTerms.orientation).map(
                    (orientation) => (
                      <label
                        key={orientation}
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={filters.orientations.includes(orientation)}
                          onChange={() =>
                            handleFilterToggle("orientations", orientation)
                          }
                          className="accent-[#0BA6C7]"
                        />
                        {orientation}
                      </label>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tags.length > 0 &&
          tags.map((tagGroup) => (
            <div
              key={tagGroup.category}
              className="rounded-[10px] p-3 bg-[#F8F8F8]"
            >
              <button
                onClick={() => toggleCategory(tagGroup.category)}
                className="w-full flex justify-between items-center text-left text-sm font-medium"
              >
                {tagGroup.category}
                {openCategory === tagGroup.category ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ArrowDown className="w-4 h-4" />
                )}
              </button>
              {openCategory === tagGroup.category && (
                <div className="mt-3 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={searchTerms.tags}
                      onChange={(e) =>
                        handleSearchChange("tags", e.target.value)
                      }
                      placeholder={`Search ${tagGroup.category.toLowerCase()}...`}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {tagGroup.values
                      .filter(
                        (value) =>
                          !searchTerms.tags ||
                          value
                            .toLowerCase()
                            .includes(searchTerms.tags.toLowerCase())
                      )
                      .map((value) => (
                        <label
                          key={`${tagGroup.category}-${value}`}
                          className="flex items-center gap-2 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={filters.tags.includes(value)}
                            onChange={() => handleFilterToggle("tags", value)}
                            className="accent-[#0BA6C7]"
                          />
                          <span>{value}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}

        {groupNames.length === 0 &&
          orientations.length === 0 &&
          tags.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No filter categories available.</p>
              <p className="text-xs mt-2">
                Add some screens with tags to see filter options.
              </p>
            </div>
          )}
      </div>

      <div className="flex gap-3 pt-4 border-t mt-4">
        <button
          onClick={handleCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          disabled={selectedFilters.length === 0}
          className="flex-1 px-4 py-2 bg-[#0BA6C7] text-white rounded-lg hover:bg-[#0991A1] disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
        >
          Apply changes
        </button>
      </div>
    </div>
  );

  return (
    <ItemListPopup title="Filter" onClose={onClose}>
      <FilterContent />
    </ItemListPopup>
  );
};

export default ScreenFilterPopup;
